import { useContext, useEffect, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { useSystemNotifications } from '../../context/SystemNotificationsContext';
import JwtUtils from '../../utils/jwtUtils';
import config from '../../config';
import { ensureNotificationSoundUnlocked, playNotificationBell, playSystemNotificationSound } from '../../services/notificationSound';
import notificationPreferenceService from '../../services/notificationPreferenceService';

// Matches the backend defaults (app/services/notification_preference_service.py)
// so a slow/failed preferences fetch keeps today's behavior: push + sound on.
const DEFAULT_PREFS = {
  new_message_push_enabled: true,
  new_message_sound_enabled: true,
};

const buildWsBase = () => {
  const apiUrl = config?.api?.apiUrl || process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v2';
  return apiUrl.replace(/^http/, 'ws');
};

const getActiveConversationFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const path = window.location?.pathname || '';
  const match = path.match(/^\/conversations\/live\/([^/]+)\/([^/]+)$/);
  if (!match) return null;
  return { clientId: match[1], conversationId: match[2] };
};

const shouldShowSystemNotification = (payload, prefs) => {
  if (!prefs?.new_message_push_enabled) return false;
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window)) return false;
  if (window.Notification.permission !== 'granted') return false;

  const isVisible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
  const hasFocus = typeof document !== 'undefined' && typeof document.hasFocus === 'function' ? document.hasFocus() : true;

  const active = getActiveConversationFromUrl();
  const incomingConversationId = payload?.conversation?.id ? String(payload.conversation.id) : null;
  if (active?.conversationId && incomingConversationId && String(active.conversationId) === incomingConversationId) {
    if (isVisible && hasFocus) {
      return false;
    }
  }

  return true;
};

const shouldPlaySound = (payload, prefs) => {
  if (!prefs?.new_message_sound_enabled) return false;
  if (typeof window === 'undefined') return false;

  const active = getActiveConversationFromUrl();
  const incomingConversationId = payload?.conversation?.id
    ? String(payload.conversation.id)
    : null;

  const isVisible = document.visibilityState === 'visible';
  const hasFocus = document.hasFocus?.() ?? true;

  // Pas de son uniquement si l'utilisateur EST dans cette conversation ET la voit
  if (
    active?.conversationId &&
    incomingConversationId &&
    String(active.conversationId) === incomingConversationId &&
    isVisible &&
    hasFocus
  ) {
    return false;
  }

  // Dans tous les autres cas : son activé
  return true;
};

const MAX_RECONNECT_DELAY_MS = 30000;
const BASE_RECONNECT_DELAY_MS = 1000;

export default function MessagePushListener() {
  const { user } = useContext(AuthContext);
  const { addMessageNotification } = useNotification();
  const { handlePushNotification: handleSystemPush } = useSystemNotifications();
  const wsRef = useRef(null);
  const prefsRef = useRef(DEFAULT_PREFS);

  useEffect(() => {
    if (!user?.id) return undefined;

    let stopped = false;

    // Settings > Notifications toggles (push / sound for incoming messages).
    // Best-effort: a failed fetch keeps the pre-feature defaults (push+sound on)
    // rather than silently going quiet.
    notificationPreferenceService.get()
      .then((prefs) => { if (!stopped) prefsRef.current = prefs; })
      .catch(() => {});

    // Parametres enregistres depuis la page Parametres : pris en compte aussitot, sans
    // reconnexion (les preferences n'etaient lues qu'une fois, a l'ouverture de session).
    const onPrefsUpdated = (event) => {
      if (event?.detail) prefsRef.current = { ...prefsRef.current, ...event.detail };
    };
    window.addEventListener('nixia:notification-prefs-updated', onPrefsUpdated);

    let reconnectTimer = null;
    let reconnectAttempt = 0;

    ensureNotificationSoundUnlocked();

    const permissionFlagKey = 'nixia_system_notifications_permission_requested';
    try {
      if (typeof window !== 'undefined' && 'Notification' in window) {
        const alreadyRequested = window.localStorage?.getItem(permissionFlagKey) === '1';
        if (!alreadyRequested && window.Notification.permission === 'default') {
          const requestOnce = async () => {
            try {
              window.localStorage?.setItem(permissionFlagKey, '1');
              await window.Notification.requestPermission();
            } catch {
              // noop
            } finally {
              window.removeEventListener('click', requestOnce, true);
              window.removeEventListener('keydown', requestOnce, true);
            }
          };
          window.addEventListener('click', requestOnce, true);
          window.addEventListener('keydown', requestOnce, true);
        }
      }
    } catch {
      // ignore permission prompting failures
    }

    const connect = () => {
      if (stopped) return;

      const token = JwtUtils.getStoredToken();
      const base = buildWsBase();
      const wsUrl = token
        ? `${base}/ws/ws/${user.id}?token=${encodeURIComponent(token)}`
        : `${base}/ws/ws/${user.id}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttempt = 0;
      };

      ws.onmessage = (event) => {
        let payload;
        try {
          payload = JSON.parse(event.data);
        } catch {
          return;
        }

        if (payload?.type !== 'push_notification') return;

        if (payload?.notification_type === 'incoming_message') {
          addMessageNotification(payload, true);
          if (shouldPlaySound(payload, prefsRef.current)) {
            playNotificationBell();
          }
        }

        if (payload?.notification_type === 'system_notification') {
          handleSystemPush(payload?.notification);
          if (prefsRef.current?.new_message_sound_enabled) {
            playSystemNotificationSound();
          }
        }

        // Deal/workflow notification routed to the push channel by the
        // recipient's "Notifications de deal" setting (default: email,
        // handled server-side - this only fires when they chose push).
        if (payload?.notification_type === 'deal_notification') {
          if (prefsRef.current?.new_message_push_enabled && typeof window !== 'undefined'
              && 'Notification' in window && window.Notification.permission === 'granted') {
            try {
              const dealNotif = new window.Notification(payload?.title || 'Notification deal', {
                body: payload?.body || '',
                tag: payload?.deal_id ? `deal-${payload.deal_id}` : undefined,
              });
              setTimeout(() => { try { dealNotif.close(); } catch { /* noop */ } }, 10000);
            } catch { /* ignore system notification errors */ }
          }
          if (prefsRef.current?.new_message_sound_enabled) {
            playSystemNotificationSound();
          }
        }

        if (payload?.notification_type !== 'incoming_message') return;

        if (!shouldShowSystemNotification(payload, prefsRef.current)) return;

        try {
          const clientName = payload?.client?.name || 'Nouveau message';
          const conversationName = payload?.conversation?.name || '';
          const preview = payload?.message?.preview || '';
          const body = conversationName ? `${conversationName} - ${preview}` : preview;
          const tag = payload?.conversation?.id ? `conv-${payload.conversation.id}` : undefined;

          const sysNotif = new window.Notification(clientName, {
            body,
            tag,
            renotify: true
          });

          sysNotif.onclick = () => {
            try {
              window.focus();
              const clientId = payload?.client?.id;
              const conversationId = payload?.conversation?.id;
              if (clientId && conversationId) {
                window.location.href = `/conversations/live/${clientId}/${conversationId}`;
              }
            } finally {
              sysNotif.close();
            }
          };

          setTimeout(() => {
            try {
              sysNotif.close();
            } catch {
              // noop
            }
          }, 10000);
        } catch {
          // Ignore system notification errors (permissions, browser restrictions, etc.)
        }
      };

      ws.onerror = () => {};
      ws.onclose = () => {
        if (wsRef.current === ws) wsRef.current = null;
        if (stopped) return;

        // Reconnect with exponential backoff so a transient network blip or
        // server restart doesn't silently kill real-time push notifications
        // until the user manually reloads the page.
        const delay = Math.min(
          BASE_RECONNECT_DELAY_MS * 2 ** reconnectAttempt,
          MAX_RECONNECT_DELAY_MS
        );
        reconnectAttempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      stopped = true;
      window.removeEventListener('nixia:notification-prefs-updated', onPrefsUpdated);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          // noop
        }
        wsRef.current = null;
      }
    };
  }, [user?.id, addMessageNotification]);

  return null;
}
